/**
 * Cross the Avenue (owner, Sep 28: 'a Frogger-style "cross the avenue"') — on the TV: six lanes of the city's traffic and a median,
 * and you on foot on the near kerb. Hop across (↑ ↓ ← →, or a tap for a hop forward and a swipe for the others) before the walk
 * light's twenty seconds run out; every crossing speeds the traffic up. Three lives.
 */
import { clamp, core, hurt, pop, rint, rnd, tick, D, L, R, U, type Core, type MiniGame } from './core';
import { box, carTop, disc, halftone, hud, INK, pops, shout } from './draw';

const W = 320, H = 180, CELL = 20, COLS = 16, ROWS = 9, START_ROW = 8, HOP_S = 0.12;
export const CROSS_S = 20;
const LANE_ROWS = [1, 2, 3, 5, 6, 7];
interface Veh { x: number; len: number; c: string }
interface Lane { row: number; dir: 1 | -1; v: number; cars: Veh[] }
export interface Cross extends Core { col: number; row: number; fromCol: number; fromRow: number; hop: number; lanes: Lane[]; timer: number; level: number; best: number }
const COLORS = ['#3a8ee8', '#e8413a', '#4ec27a', '#e8e4d8', '#9a5ad8', '#f08a2a'];

function layLanes(s: Cross): void {
  s.lanes = LANE_ROWS.map((row, i) => {
    const dir = (i % 2 ? 1 : -1) as 1 | -1, v = (26 + rnd(s) * 30) * (1 + s.level * 0.12), cars: Veh[] = [];
    let x = rnd(s) * 60;
    while (x < W + 60) { const bus = rnd(s) < 0.2, len = bus ? 44 : 24; cars.push({ x, len, c: bus ? '#f2c230' : COLORS[rint(s, 0, COLORS.length - 1)] }); x += len + 40 + rnd(s) * 70; }
    return { row, dir, v, cars };
  });
}
function home(s: Cross): void { s.col = s.fromCol = 7; s.row = s.fromRow = START_ROW; s.hop = 0; s.timer = CROSS_S; s.best = START_ROW; }
/** where the walker is drawn / tested: between the two cells while hopping */
export function walkerAt(s: Cross): { x: number; y: number } { const k = s.hop > 0 ? 1 - s.hop / HOP_S : 1; return { x: (s.fromCol + (s.col - s.fromCol) * k + 0.5) * CELL, y: (s.fromRow + (s.row - s.fromRow) * k + 0.5) * CELL }; }
/** a car overlapping this x in this row (the cars' x is their middle) */
export function carAt(s: Cross, row: number, x: number, pad = 6): boolean { const l = s.lanes.find((q) => q.row === row); return !!l && l.cars.some((c) => Math.abs(c.x - x) < c.len / 2 + pad); }

export const cross: MiniGame<Cross> = {
  id: 'cross_the_avenue', name: 'Cross the Avenue', icon: '🚸', color: '#2a8a4a', where: 'tv',
  blurb: 'Six lanes, a walk light that runs out, and you on foot.',
  keys: '↑ ↓ ← → hop', touch: 'tap hops forward · swipe hops that way',
  w: W, h: H, maxRate: 320, maxScore: 1000000,
  init(seed) { const s = core(seed, 3) as Cross; Object.assign(s, { level: 0, lanes: [] }); home(s); layLanes(s); return s; },
  step(s, inp, dt) {
    tick(s, dt); if (s.over) return;
    for (const l of s.lanes) for (const c of l.cars) { c.x += l.dir * l.v * dt; if (l.dir > 0 && c.x - c.len / 2 > W + 30) c.x -= W + 60 + c.len; if (l.dir < 0 && c.x + c.len / 2 < -30) c.x += W + 60 + c.len; }
    s.timer -= dt;
    if (s.hop > 0) s.hop = Math.max(0, s.hop - dt);
    else {
      const b = inp.press | (inp.ptap ? U : 0);
      const [dc, dr] = b & U ? [0, -1] : b & D ? [0, 1] : b & L ? [-1, 0] : b & R ? [1, 0] : [0, 0];
      if (dc || dr) { s.fromCol = s.col; s.fromRow = s.row; s.col = clamp(s.col + dc, 0, COLS - 1); s.row = clamp(s.row + dr, 0, ROWS - 1); if (s.col !== s.fromCol || s.row !== s.fromRow) { s.hop = HOP_S; s.fx.push('blip'); } }
    }
    const at = walkerAt(s), row = Math.round(at.y / CELL - 0.5);
    if (carAt(s, row, at.x, 5)) { hurt(s, 5); s.fx.push('honk'); pop(s, at.x, at.y, 'SPLAT!', '#ff4a3a'); if (!s.over) home(s); return; }
    if (s.hop === 0 && s.row < s.best) { s.best = s.row; s.score += 10; }
    if (s.hop === 0 && s.row === 0) { s.score += 100 + Math.ceil(Math.max(0, s.timer)) * 5; s.level++; s.fx.push('win'); pop(s, at.x, at.y - 6, 'SAFE!', '#4ec27a'); for (const l of s.lanes) l.v *= 1.12; home(s); return; }
    if (s.timer <= 0) { hurt(s); pop(s, at.x, at.y, 'TOO SLOW!', '#ff8a2a'); if (!s.over) home(s); }
  },
  draw(ctx, s) {
    // the kerbs and the median (paving, halftone), the six lanes, their lines
    ctx.fillStyle = '#3b3f4a'; ctx.fillRect(0, 0, W, H);
    for (const r of [0, 4, START_ROW]) { ctx.fillStyle = r === 4 ? '#8ab86a' : '#c9c2b2'; ctx.fillRect(0, r * CELL, W, CELL); halftone(ctx, 0, r * CELL, W, CELL, 'rgba(0,0,0,0.12)', 6, 1); ctx.fillStyle = INK; ctx.fillRect(0, r * CELL + (r === 0 ? CELL - 2 : 0), W, 2); if (r === 4) ctx.fillRect(0, r * CELL + CELL - 2, W, 2); }
    ctx.setLineDash([10, 10]); ctx.strokeStyle = '#f4f0e0'; ctx.lineWidth = 1.5;
    for (const y of [2, 3, 6, 7]) { ctx.beginPath(); ctx.moveTo(0, y * CELL); ctx.lineTo(W, y * CELL); ctx.stroke(); }
    ctx.setLineDash([]);
    shout(ctx, `SAFE SIDE · CROSSING ${s.level + 1}`, W / 2, CELL / 2, 9, '#4ec27a');
    for (const l of s.lanes) for (const c of l.cars) carTop(ctx, c.x, (l.row + 0.5) * CELL, c.len, 13, l.dir > 0 ? 0 : Math.PI, c.c);
    // you: a head, a jacket, a bob while hopping
    const at = walkerAt(s), bob = s.hop > 0 ? Math.sin((1 - s.hop / HOP_S) * Math.PI) * 4 : 0;
    ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.beginPath(); ctx.ellipse(at.x, at.y + 5, 6, 2.5, 0, 0, Math.PI * 2); ctx.fill();
    box(ctx, at.x - 5, at.y - 3 - bob, 10, 9, '#e8413a', 3); disc(ctx, at.x, at.y - 6 - bob, 4, '#f2c8a0', 1.3);
    // the walk light's time
    const k = clamp(s.timer / CROSS_S, 0, 1); box(ctx, 6, H - 7, (W - 12) * k, 4, k > 0.3 ? '#ffe14a' : '#ff4a3a', 2, 1);
    pops(ctx, s);
    hud(ctx, s, W);
  },
};
